// src/pages/auth/VerifyEmailPage.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../../components/marketing/Navbar';

const VerifyEmailPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [redirectTo, setRedirectTo] = useState('/login');
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Validate token exists
    if (!token) {
      setStatus('error');
      setMessage('Geen verificatietoken gevonden in de URL.');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.lerenmotiveren.nl'}/api/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setStatus('success');
          setMessage(data.message || 'E-mail succesvol geverifieerd!');
          const redirect = data.redirectTo || '/login';
          setRedirectTo(redirect);
          
          // Auto redirect after 3 seconds
          timeoutRef.current = setTimeout(() => {
            navigate(redirect, { replace: true });
          }, 3000);
        } else {
          setStatus('error');
          setMessage(data.message || 'Verificatie mislukt. De link is mogelijk verlopen.');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('Er is een fout opgetreden. Controleer je internetverbinding en probeer het opnieuw.');
      }
    };

    verifyEmail();

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8 mt-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
              <h1 className="text-2xl font-light text-purple-800 mb-2">
                E-mail Verificatie
              </h1>
              <p className="text-gray-600">
                Bezig met verifiëren van je e-mailadres...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-light text-purple-800 mb-2">
                Verificatie Succesvol!
              </h1>
              <p className="text-gray-600 mb-4">
                {message}
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-purple-900">
                  Je wordt automatisch doorgestuurd in 3 seconden...
                </p>
              </div>
              <Link
                to={redirectTo}
                className="inline-block px-6 py-2 bg-purple-500 text-white font-semibold rounded-md hover:bg-purple-600 transition-colors"
              >
                Direct doorgaan
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-light text-purple-800 mb-2">
                Verificatie Mislukt
              </h1>
              <p className="text-gray-600 mb-4">
                {message}
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-900 mb-2">
                  <strong>Mogelijke oorzaken:</strong>
                </p>
                <ul className="text-left text-sm text-red-800 space-y-1">
                  <li>• De verificatielink is verlopen</li>
                  <li>• Je e-mailadres is al geverifieerd</li>
                  <li>• De link is al gebruikt</li>
                </ul>
              </div>
              <div className="space-y-3">
                <Link
                  to="/login"
                  className="block px-6 py-2 bg-purple-500 text-white font-semibold rounded-md hover:bg-purple-600 transition-colors"
                >
                  Naar Inloggen
                </Link>
                <button
                  onClick={() => window.location.reload()}
                  className="block w-full px-6 py-2 border border-purple-500 text-purple-600 font-semibold rounded-md hover:bg-purple-50 transition-colors"
                >
                  Opnieuw Proberen
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;